import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

type StrengthLevel = 0 | 1 | 2 | 3 | 4;

/**
 * Visual password strength meter. Mirrors backend complexity rules
 * (8+ chars, upper/lower/digit/special) so users see the same bar the
 * server will enforce on submit.
 */
@Component({
  selector: 'vh-password-strength',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (show()) {
      <div class="vh-pw">
        <div class="vh-pw__bars" [attr.data-level]="level()">
          @for (i of segments; track i) {
            <span class="vh-pw__bar" [class.vh-pw__bar--active]="i < level()"></span>
          }
        </div>
        <span class="vh-pw__label">{{ label() }}</span>
      </div>
    }
  `,
  styleUrl: './password-strength.component.scss',
})
export class VhPasswordStrengthComponent {
  readonly password = input<string>('');

  readonly segments = [0, 1, 2, 3] as const;

  readonly show = computed(() => this.password().length > 0);

  readonly level = computed<StrengthLevel>(() => {
    const pw = this.password();
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return Math.max(0, Math.min(4, score)) as StrengthLevel;
  });

  readonly label = computed(() => {
    switch (this.level()) {
      case 0:
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
    }
  });
}
